import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';

export interface ILData {
  date: string;
  il_score: number;
  diet_score: number;
  sleep_score: number;
  stress_score: number;
  hrv_score: number | null;
  activity_score: number;
  alcohol_score: number;
  smoking_score: number;
  hydration_score: number;
  social_connection_score: number;
  contributors: string[];
}

export function useILScore(days: number = 30) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['il-scores', days],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
      
      const { data: ilData, error } = await supabase
        .from('il_daily')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .order('date', { ascending: false });

      if (error) throw error;
      return ilData as ILData[];
    }
  });

  const computeScore = useMutation({
    mutationFn: async (date?: string) => {
      const { data, error } = await supabase.functions.invoke('compute-il-daily', {
        body: { date: date || format(new Date(), 'yyyy-MM-dd') }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['il-scores'] });
    }
  });

  const todayScore = data?.[0];
  const sevenDayAvg = data && data.length >= 7 
    ? data.slice(0, 7).reduce((sum, d) => sum + d.il_score, 0) / 7 
    : null;
  const thirtyDayAvg = data && data.length >= 30 
    ? data.slice(0, 30).reduce((sum, d) => sum + d.il_score, 0) / 30 
    : null;

  return {
    data,
    isLoading,
    todayScore,
    sevenDayAvg,
    thirtyDayAvg,
    computeScore: computeScore.mutate,
    isComputing: computeScore.isPending
  };
}
